import { injectable } from 'tsyringe';
import {
    type EnrichedPool,
    type LicenseAssignment,
    type BulkResult,
    type LicenseCostRecord,
    type LicenseOptimizationDto,
    type GetApiLicensesCostSummary200ResponseByPlatformInner,
    type PaginatedResponse,
    type SyncOutcome,
    type TenantSyncOutcome,
    ResponseError,
} from '@bmai/licenses-api-client';
import { licensesClient } from '@/shared/LicensesApiClient';
import { connectionsClient } from '@/shared/ConnectionsApiClient';
import type { ISignalLicenseSource } from '@/shared/signals/SignalTypes';

export interface LicenseChurn {
    reclaimed: number;
    assigned: number;
    windowDays: number;
}

/** One admin-entered seat/subscription cost line. The generated client types the request
 * body's `entries` as unspecified, so the shape is pinned here (mirrors the API contract). */
export interface SeatCostEntryInput {
    providerConnectionId: string;
    periodStart: string;
    periodEnd: string;
    amount: number;
    currency: string;
    skuId?: string;
    seatCount?: number;
    description?: string;
}

/** Display metadata for a provider connection — covers usage-only connections that have no license pool. */
export interface ConnectionNameEntry {
    displayName: string;
    providerTypeId: string;
}

export type LicensePool = EnrichedPool;
export type PaginatedAssignments = PaginatedResponse;
export type PlatformSpend = GetApiLicensesCostSummary200ResponseByPlatformInner;
export type { LicenseAssignment, BulkResult, SyncOutcome, TenantSyncOutcome, LicenseOptimizationDto, LicenseCostRecord };

@injectable()
export class LicensesApiService implements ISignalLicenseSource {
    private readonly api = licensesClient.licensesApi;

    async listPools(): Promise<LicensePool[]> {
        return runApi('list pools', async () => {
            const { pools } = await this.api.getPools();
            return pools;
        });
    }

    async getAssignments(connectionId: string, page: number = 1, pageSize: number = 50): Promise<PaginatedAssignments> {
        return runApi('get assignments', () =>
            this.api.getPoolsByConnectionIdAssignments({
                connectionId,
                page: String(page),
                pageSize: String(pageSize),
            }),
        );
    }

    async listAllAssignments(): Promise<LicenseAssignment[]> {
        const pools = await this.listPools();
        const all: LicenseAssignment[] = [];
        for (const pool of pools) {
            for (let page = 1; ; page += 1) {
                const result = await this.getAssignments(pool.providerConnectionId, page, 200);
                all.push(...result.items);
                const totalPages = Number.isFinite(result.totalPages) ? result.totalPages : page;
                if (result.items.length === 0 || page >= totalPages) break;
            }
        }
        return all;
    }

    async bulkAssign(connectionId: string, userIds: string[], role?: string): Promise<BulkResult> {
        return runApi('bulk-assign', () =>
            this.api.postPoolsByConnectionIdBulkAssign({ connectionId, bulkBody: buildBulkBody(userIds, role) }),
        );
    }

    async bulkUnassign(connectionId: string, userIds: string[]): Promise<BulkResult> {
        return runApi('bulk-unassign', () =>
            this.api.postPoolsByConnectionIdBulkUnassign({ connectionId, bulkBody: buildBulkBody(userIds) }),
        );
    }

    async getOptimization(days: number = 30): Promise<LicenseOptimizationDto> {
        return runApi('get optimization', () => this.api.getOptimization({ days: String(days) }));
    }

    async getConnectionNames(): Promise<Map<string, ConnectionNameEntry>> {
        const connections = await connectionsClient.providerConnectionsApi.getProviderConnections();
        const names = new Map<string, ConnectionNameEntry>();
        for (const connection of connections) {
            const name = connection.displayName;
            if (typeof name === 'string' && name.length > 0) {
                names.set(connection.id, { displayName: name, providerTypeId: String(connection.providerTypeId) });
            }
        }
        return names;
    }

    async getPlatformSpend(periodStart: string, periodEnd: string): Promise<PlatformSpend[]> {
        return runApi('get cost summary', async () => {
            const { byPlatform } = await this.api.getCostSummary({ periodStart, periodEnd });
            return byPlatform;
        });
    }

    async getCostRecords(periodStart: string, periodEnd: string, limit: number = 1000): Promise<LicenseCostRecord[]> {
        return runApi('get cost records', async () => {
            const { records } = await this.api.getCostRecords({ periodStart, periodEnd, limit: String(limit) });
            return records;
        });
    }

    async recordSeatCosts(entries: SeatCostEntryInput[]): Promise<number> {
        return runApi('record seat costs', async () => {
            const { written } = await this.api.postSeatCosts({ seatCostsBody: { entries } });
            return written;
        });
    }

    async syncTenant(): Promise<TenantSyncOutcome> {
        return runApi('sync tenant', () => this.api.postSync());
    }

    async syncConnection(connectionId: string): Promise<SyncOutcome> {
        return runApi('sync connection', () => this.api.postPoolsByConnectionIdSync({ connectionId }));
    }
}

function buildBulkBody(userIds: string[], role?: string): { userIds: string[]; role?: string } {
    return role !== undefined && role.length > 0 ? { userIds, role } : { userIds };
}

async function runApi<T>(op: string, call: () => Promise<T>): Promise<T> {
    try {
        return await call();
    } catch (cause) {
        if (cause instanceof ResponseError) {
            throw new Error(await readError(cause.response, op));
        }
        throw cause;
    }
}

async function readError(response: Response, op: string): Promise<string> {
    try {
        const body = (await response.clone().json()) as { error?: string };
        if (typeof body.error === 'string' && body.error.length > 0) return body.error;
    } catch (cause) {
        console.debug('[LicensesApi] non-JSON error body', { op, status: response.status, cause });
    }
    return `${op} failed (${response.status} ${response.statusText})`;
}
